import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { apiGet, apiPost } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, CheckCircle, XCircle, RotateCcw } from 'lucide-react';
import { buddyEvents } from '@/components/TutorBuddy';
import { Confetti } from '@/components/MicroWins';
import BuildingDeckSplash from '@/components/BuildingDeckSplash';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import AhaSummaryCard from '@/components/AhaSummaryCard';
import ActiveScopeChip from '@/components/ActiveScopeChip';

const playPopSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.value = 800; gain.gain.value = 0.3;
    osc.start(); gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
    osc.stop(ctx.currentTime + 0.15);
  } catch {}
};

export default function QuizPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState('select'); // select, playing, results
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  // Phase 2.71 — pre-fill Subject + Topic from the top-bar dropdown so the
  // student doesn't have to re-pick what they already chose seconds ago.
  // Snapshotted at mount so a mid-page top-bar change doesn't reset their
  // in-progress selection.
  const [selectedSubject, setSelectedSubject] = useState(() => {
    try { return window.localStorage.getItem('gv_active_subject') || ''; }
    catch { return ''; }
  });
  const [selectedTopic, setSelectedTopic] = useState(() => {
    try { return window.localStorage.getItem('gv_active_topic') || ''; }
    catch { return ''; }
  });
  const [quiz, setQuiz] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showAnswer, setShowAnswer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [showSummary, setShowSummary] = useState(false);

  useEffect(() => {
    if (user?.class_level) {
      const boardParam = user?.board ? `&board=${encodeURIComponent(user.board)}` : '';
      apiGet(`/onboarding/subjects?class_level=${user.class_level}${boardParam}`).then(d => setSubjects(d.subjects || [])).catch(() => {});
    }
  }, [user]);

  // Phase 2.72 — Auto-hydrate a quiz from the chapter pre-warm cache the
  // moment the student lands on /quiz with a top-bar subject + topic
  // selected. The cache is pre-populated when the admin approves the
  // syllabus chapter (visible as the green "Quiz" badge in Content Tree),
  // so this is an instant zero-LLM-cost serve. Student goes straight from
  // dashboard to playing the quiz — no "Quick Quiz" form to fill in.
  // Guarded by `autoHydratedRef` so it fires once per page mount.
  const autoHydratedRef = useRef(false);
  const [autoHydrating, setAutoHydrating] = useState(false);
  useEffect(() => {
    if (autoHydratedRef.current) return;
    if (phase !== 'select') return;
    if (!selectedSubject || !selectedTopic) return;
    if (!user?.class_level) return;
    autoHydratedRef.current = true;
    setAutoHydrating(true);
    const startTs = performance.now();
    (async () => {
      try {
        const q = await apiPost('/quiz/generate', {
          subject: selectedSubject,
          topic: selectedTopic,
          board: user?.board || '',
          class_level: String(user.class_level || ''),
        });
        if (q && q.questions && q.questions.length > 0) {
          setQuiz(q);
          setPhase('playing');
          setCurrentQ(0);
          setAnswers({});
          // Phase 2.73 — cheap dopamine. Tells the student "we just
          // assembled this FOR YOU, fast". Skipped when the call failed
          // (no cache yet) so we never lie about success.
          const elapsedS = Math.max(0.1, (performance.now() - startTs) / 1000);
          toast.success(
            `Ready in ${elapsedS.toFixed(1)}s — quiz pre-assembled for ${selectedSubject} · ${selectedTopic}`,
            { duration: 2400 },
          );
        }
      } catch { /* cache empty / tier-blocked — student can still click Generate manually */ }
      finally { setAutoHydrating(false); }
    })();
  }, [phase, selectedSubject, selectedTopic, user]);

  useEffect(() => {
    if (selectedSubject && user?.class_level) {
      const boardParam = user?.board ? `&board=${encodeURIComponent(user.board)}` : '';
      apiGet(`/onboarding/topics?subject=${encodeURIComponent(selectedSubject)}&class_level=${user.class_level}${boardParam}`).then(d => setTopics(d.topics || [])).catch(() => {});
    }
  }, [selectedSubject, user]);

  const generateQuiz = async () => {
    if (!selectedSubject || !selectedTopic) { toast.error('Please select subject and topic'); return; }
    setLoading(true);
    try {
      const q = await apiPost('/quiz/generate', { subject: selectedSubject, topic: selectedTopic });
      setQuiz(q);
      setPhase('playing');
      setCurrentQ(0);
      setAnswers({});
    } catch (e) {
      const msg = e.message || '';
      if (msg.includes('limit')) toast.error('Daily quiz limit reached!');
      else if (msg.includes('disabled')) toast.error('Quizzes are currently disabled');
      else toast.error('Failed to generate quiz');
    }
    setLoading(false);
  };

  const selectAnswer = (optionIdx) => {
    if (showAnswer) return;
    const isCorrect = quiz.questions[currentQ].correct_index === optionIdx;
    setAnswers(prev => ({ ...prev, [currentQ]: optionIdx }));
    setShowAnswer(true);
    if (isCorrect) {
      playPopSound();
      confetti({ particleCount: 50, spread: 50, origin: { y: 0.7 }, colors: ['#10B981', '#3B82F6', '#F59E0B'] });
      buddyEvents.celebrate('Bullseye! Keep it going.');
    } else {
      buddyEvents.console("Close! Read the explanation\u2014you'll lock it in.");
    }
  };

  const nextQuestion = () => {
    setShowAnswer(false);
    if (currentQ < quiz.questions.length - 1) {
      setCurrentQ(c => c + 1);
    } else {
      submitQuiz();
    }
  };

  const submitQuiz = async () => {
    try {
      const res = await apiPost(`/quiz/${quiz.quiz_id}/submit`, { answers });
      setResults(res);
      setPhase('results');
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
      const pct = Math.round(((res?.score || 0) / Math.max(1, res?.total || 1)) * 100);
      if (pct >= 80) buddyEvents.celebrate(`${pct}% — you\u2019re flying!`);
      else if (pct >= 50) buddyEvents.say(`${pct}% — solid! One more round and you\u2019ll ace it.`, 'happy', 5000);
      else buddyEvents.console(`${pct}% — every miss is a clue. Want me to break this topic down?`);
    } catch { toast.error('Failed to submit quiz'); }
  };

  // SELECT phase
  if (phase === 'select') {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6" data-testid="quiz-select-page">
        <div className="flex items-center gap-3 mb-6">
          <button data-testid="quiz-back-btn" onClick={() => navigate('/dashboard')} className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <h1 className="font-heading text-xl font-bold text-slate-900">Quick Quiz</h1>
        </div>
        {autoHydrating ? (
          <BuildingDeckSplash kind="quiz" subject={selectedSubject} topic={selectedTopic} />
        ) : (
        <>
        {/* Phase 2.71 — chapter-scope chip with Clear filter */}
        <ActiveScopeChip
          subject={selectedSubject}
          topic={selectedTopic}
          onClear={() => { setSelectedSubject(''); setSelectedTopic(''); }}
        />
        <div className="glass-panel rounded-3xl p-6 space-y-4">
          <select data-testid="quiz-subject-select" value={selectedSubject} onChange={e => { setSelectedSubject(e.target.value); setSelectedTopic(''); }}
            className="w-full p-3 rounded-2xl border-2 border-slate-200 bg-white text-sm font-body focus:border-blue-500 focus:outline-none">
            <option value="">Select Subject</option>
            {subjects.map(s => <option key={s.subject_id} value={s.name}>{s.name}</option>)}
          </select>
          {selectedSubject && (
            <select data-testid="quiz-topic-select" value={selectedTopic} onChange={e => setSelectedTopic(e.target.value)}
              className="w-full p-3 rounded-2xl border-2 border-slate-200 bg-white text-sm font-body focus:border-blue-500 focus:outline-none">
              <option value="">Select Topic</option>
              {/* Phase 2.71 — keep the pre-filled top-bar topic visible
                  even when it's not in the canonical /onboarding/topics
                  list (e.g. it came from an admin-curated syllabus
                  chapter that doesn't have a matching topics row). */}
              {selectedTopic && !topics.some(t => t.name === selectedTopic) && (
                <option key="from-topbar" value={selectedTopic}>{selectedTopic} (from top bar)</option>
              )}
              {topics.map(t => <option key={t.topic_id} value={t.name}>{t.name}</option>)}
            </select>
          )}
          <Button data-testid="generate-quiz-btn" onClick={generateQuiz} disabled={loading || !selectedSubject || !selectedTopic}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full font-heading">
            {loading ? 'Generating Quiz...' : 'Generate Quiz'}
          </Button>
        </div>
        </>
        )}
      </div>
    );
  }

  // PLAYING phase
  if (phase === 'playing' && quiz) {
    const question = quiz.questions[currentQ];
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6" data-testid="quiz-playing-page">
        <div className="flex items-center justify-between mb-4">
          <Badge className="bg-blue-50 text-blue-600 font-body">{quiz.subject} &middot; {quiz.topic}</Badge>
          <span className="text-sm font-body text-slate-500">{currentQ + 1} / {quiz.questions.length}</span>
        </div>
        <Progress value={((currentQ + 1) / quiz.questions.length) * 100} className="h-2 mb-6" data-testid="quiz-progress" />

        <Card className="p-6 rounded-2xl border-slate-100 mb-4 animate-slide-up">
          <p className="font-heading text-lg font-semibold text-slate-800 mb-6" data-testid="quiz-question">{question?.question}</p>
          <div className="space-y-3">
            {question?.options?.map((opt, i) => {
              const isSelected = answers[currentQ] === i;
              const isCorrect = question.correct_index === i;
              let optClass = 'quiz-option p-4 rounded-2xl border-2 cursor-pointer text-left w-full';
              if (showAnswer) {
                if (isCorrect) optClass += ' correct border-emerald-500 bg-emerald-50';
                else if (isSelected && !isCorrect) optClass += ' incorrect border-red-500 bg-red-50 animate-shake';
                else optClass += ' border-slate-200 opacity-60';
              } else {
                optClass += isSelected ? ' border-blue-500 bg-blue-50' : ' border-slate-200 hover:border-slate-300';
              }
              return (
                <button key={i} data-testid={`quiz-option-${i}`} onClick={() => selectAnswer(i)} className={optClass} disabled={showAnswer}>
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-sm font-heading font-semibold text-slate-600 flex-shrink-0">
                      {String.fromCharCode(65 + i)}
                    </span>
                    <span className="text-sm font-body text-slate-700">{opt}</span>
                    {showAnswer && isCorrect && <CheckCircle className="w-5 h-5 text-emerald-500 ml-auto flex-shrink-0" />}
                    {showAnswer && isSelected && !isCorrect && <XCircle className="w-5 h-5 text-red-500 ml-auto flex-shrink-0" />}
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        {showAnswer && (
          <div className="animate-slide-up">
            <Card className="p-4 rounded-2xl border-slate-100 mb-4 bg-blue-50/50">
              <p className="text-sm font-body text-slate-700"><span className="font-semibold">Explanation:</span> {question?.explanation}</p>
            </Card>
            <Button data-testid="quiz-next-btn" onClick={nextQuestion} className="w-full bg-blue-500 hover:bg-blue-600 text-white rounded-full font-heading">
              {currentQ < quiz.questions.length - 1 ? 'Next Question' : 'See Results'}
            </Button>
          </div>
        )}
      </div>
    );
  }

  // RESULTS phase
  if (phase === 'results' && results) {
    const percentage = Math.round((results.score / results.total) * 100);
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6" data-testid="quiz-results-page">
        <Card className="p-8 rounded-3xl border-slate-100 text-center animate-slide-up">
          <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${percentage >= 70 ? 'bg-emerald-50' : percentage >= 40 ? 'bg-amber-50' : 'bg-red-50'}`}>
            <span className={`font-heading text-3xl font-bold ${percentage >= 70 ? 'text-emerald-600' : percentage >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
              {percentage}%
            </span>
          </div>
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-1">
            {percentage >= 70 ? 'Awesome Job!' : percentage >= 40 ? 'Good Effort!' : 'Keep Practicing!'}
          </h2>
          <p className="text-sm text-slate-500 font-body mb-6">You got {results.score} out of {results.total} correct</p>
          <div className="flex gap-3">
            <Button data-testid="quiz-retry-btn" onClick={() => { setPhase('select'); setQuiz(null); setResults(null); }}
              variant="outline" className="flex-1 rounded-full font-heading">
              <RotateCcw className="w-4 h-4 mr-2" /> Try Again
            </Button>
            <Button data-testid="quiz-done-btn" onClick={() => navigate('/dashboard')}
              className="flex-1 bg-blue-500 hover:bg-blue-600 text-white rounded-full font-heading">
              Done
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return null;
}
